import { useEffect, useState } from "react"
import { Alert, Button, Col, Container, FloatingLabel, Form, Row, Spinner } from "react-bootstrap"
import { apiClient } from "../services/api/client"

const EMPTY_FORM = {
  id_strediska: null,
  cislo: "",
  nazev: "",
  stani: "",
  sklad_mrp: "",
  prefix_mrp: "",
}

const Departments = ({ tabActive }) => {
  const [form, setForm] = useState(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [validated, setValidated] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const loadDepartment = async ({ clearMessages = true } = {}) => {
    setLoading(true)
    if (clearMessages) {
      setError("")
      setSuccess("")
    }
    setValidated(false)
    try {
      const list = await apiClient.get("/strediska")
      const item = Array.isArray(list) && list.length > 0 ? list[0] : null
      if (!item) {
        setForm(EMPTY_FORM)
        return
      }
      setForm({
        id_strediska: item.id_strediska ?? null,
        cislo: String(item.cislo ?? ""),
        nazev: String(item.nazev ?? ""),
        stani: String(item.stani ?? ""),
        sklad_mrp: String(item.sklad_mrp ?? ""),
        prefix_mrp: String(item.prefix_mrp ?? ""),
      })
    } catch (requestError) {
      setError(requestError.message || "Nepodařilo se načíst středisko.")
      setForm(EMPTY_FORM)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!tabActive) return
    loadDepartment()
  }, [tabActive])

  const handleChange = (event) => {
    const { name, value } = event.target
    const nextValue = name === "prefix_mrp" ? value.replace(/\s+/g, "").toUpperCase() : value
    setForm((prev) => ({ ...prev, [name]: nextValue }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const formElement = event.currentTarget
    const isValid = formElement.checkValidity()
    setValidated(true)
    setError("")
    setSuccess("")
    if (!isValid) return

    const payload = {
      cislo: Number(form.cislo),
      nazev: String(form.nazev || "").trim(),
      stani: Number(form.stani),
      sklad_mrp: Number(form.sklad_mrp),
      prefix_mrp: String(form.prefix_mrp || "").trim().toUpperCase(),
    }

    setSaving(true)
    try {
      if (form.id_strediska) {
        const verify = await apiClient.get(
          `/strediska/overit/id/${form.id_strediska}?cislo=${encodeURIComponent(payload.cislo)}&nazev=${encodeURIComponent(payload.nazev)}&stani=${encodeURIComponent(payload.stani)}&sklad_mrp=${encodeURIComponent(payload.sklad_mrp)}&prefix_mrp=${encodeURIComponent(payload.prefix_mrp)}`
        )
        if (verify?.exists) {
          setError("Záznam se stejným číslem střediska, číslem skladu, prefixem nebo názvem již existuje.")
          return
        }
        await apiClient.put(`/strediska/${form.id_strediska}`, payload)
        setSuccess("Středisko bylo upraveno.")
      } else {
        const verify = await apiClient.get(
          `/strediska/overit?cislo=${encodeURIComponent(payload.cislo)}&nazev=${encodeURIComponent(payload.nazev)}&stani=${encodeURIComponent(payload.stani)}&sklad_mrp=${encodeURIComponent(payload.sklad_mrp)}&prefix_mrp=${encodeURIComponent(payload.prefix_mrp)}`
        )
        if (verify?.exists) {
          setError("Záznam se stejným číslem střediska, číslem skladu, prefixem nebo názvem již existuje.")
          return
        }
        await apiClient.post("/strediska", payload)
        setSuccess("Středisko bylo uloženo.")
      }
      await loadDepartment({ clearMessages: false })
      setValidated(false)
    } catch (requestError) {
      setError(requestError.message || "Nepodařilo se uložit středisko.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container fluid className="px-0">
      <Row>
        <Col md={8} lg={6} xxl={4}>
              <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
            <div>
              <h5 className="mb-1">Středisko</h5>
              <p className="text-muted small mb-0">Konfigurace střediska.</p>
            </div>
            <Button variant="outline-secondary" onClick={loadDepartment} disabled={loading || saving}>
              Obnovit
            </Button>
          </div>

          <div>
            <p className="small text-muted mb-3">
              Ke středisku je nutné navázat číslo skladu a prefix číselné řady přijatých
              objednávek odpovídající hodnotám v účetním systému. Počet servisních stání určuje kapacitu v kalendáři
              rezervací.
            </p>

            {loading ? (
              <div className="d-flex align-items-center gap-2 text-muted small py-2">
                <Spinner size="sm" />
                Načítám středisko...
              </div>
            ) : (
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row className="g-2">
                  <Col xs={12}>
                    <FloatingLabel label="Název střediska">
                      <Form.Control
                        type="text"
                        name="nazev"
                        value={form.nazev}
                        onChange={handleChange}
                        maxLength={64}
                        required
                      />
                    </FloatingLabel>
                  </Col>

                  <Col xs={12}>
                    <FloatingLabel label="Číslo střediska">
                      <Form.Control
                        type="text"
                        name="cislo"
                        value={form.cislo}
                        onChange={handleChange}
                        pattern="^[1-9][0-9]{0,2}$"
                        title="Uveďte číslo střediska podle údajů v MRP (1 až 999)."
                        required
                      />
                    </FloatingLabel>
                  </Col>                                   

                  <Col xs={12}>
                    <FloatingLabel label="Číslo skladu">
                      <Form.Control
                        type="text"
                        name="sklad_mrp"
                        value={form.sklad_mrp}
                        onChange={handleChange}
                        pattern="^[1-9][0-9]{0,1}$"
                        title="Uveďte číslo skladu podle údajů v MRP (1 až 99)."
                        required
                      />
                    </FloatingLabel>
                  </Col>

                  <Col xs={12}>
                    <FloatingLabel label="Prefix číselné řady">
                  <Form.Control
                    type="text"
                    name="prefix_mrp"
                    value={form.prefix_mrp}
                    onChange={handleChange}
                    pattern="^Z[0-9]{4}$"
                    title="Uveďte prefix číselné řady v objednávkách přijatých (Zxxxx)."
                    required
                  />
                    </FloatingLabel>
                  </Col>

                   <Col xs={12}>
                    <FloatingLabel label="Počet stání">
                      <Form.Control
                        type="text"
                        name="stani"
                        value={form.stani}
                        onChange={handleChange}
                        pattern="^[1-9][0-9]{0,1}$"
                        title="Uveďte počet servisních stání (1 až 99)."
                        required
                      />
                    </FloatingLabel>
                  </Col>
                </Row>

                {error && (
                  <Alert variant="danger" className="mt-3 mb-0">
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert variant="success" className="mt-3 mb-0">
                    {success}
                  </Alert>
                )}

                <div className="d-flex justify-content-start mt-3">
                  <Button type="submit" variant="primary" disabled={saving || loading}>
                    {saving ? "Ukládám..." : "Uložit"}
                  </Button>
                </div>
              </Form>
            )}
          </div>        
        </Col>
      </Row>      
    </Container>
  )
}

export default Departments
